/**
 * 工作台 Tab 菜单配置
 *
 * 规则：
 * - 新增/修改菜单项只改本文件，不要修改 Menu.vue
 * - headerTabExtraMenus 用于补充未被动态路由覆盖的 fallback 菜单
 */
export interface WorkbenchMenuModule {
  label: string
  aliases?: string[]
  openInNewWindow?: boolean
  newWindowRouteName?: string
}

export interface WorkbenchMenuGroup {
  title: string
  // 分组包含的菜单标题（匹配 WorkbenchMenuModule.label 或其 aliases）
  modules: string[]
}

// 工作台 tab 下的菜单项定义
// - 第一位是「首页」,对应 /home (AdminHome),即租户管理平台首页
// - 「仪表盘」「总裁驾驶舱」标记为新窗口打开,点击时调用 window.open 跳转独立展示页
export const workbenchMenuModules: WorkbenchMenuModule[] = [
  { label: '首页' },
  { label: '仪表盘', openInNewWindow: true, newWindowRouteName: 'WorkbenchDashboardView' },
  { label: '总裁驾驶舱', openInNewWindow: true, newWindowRouteName: 'WorkbenchCeoCockpitView' },
  // F11：公共底座配置挂主壳「工作台」，不依赖开通 AI大模型/秘书应用卡
  { label: '平台 AI 配置', aliases: ['平台AI配置', 'AI 平台配置'] },
  // 评价治理三页：system_menu 已建、路由已注册，但侧栏按本表白名单过滤，不列标题即不可见
  { label: '治理工作台' },
  { label: '系统管理' },
  { label: '基础设施' },
  { label: '工作流程' },
  { label: '会员管理', aliases: ['会员中心'] },
  { label: '商家运营', aliases: ['商家主体'] },
  { label: '渠道代理商' },
  { label: '平台分销' },
  { label: '门户解决方案' },
  { label: '支付管理' },
  { label: '模板管理' },
  // 灵工：主壳工作台「业务」分类；侧栏标题须与 system_menu.name 一致
  { label: '灵工提现' },
  { label: '评价管理' },
  { label: '内容安全' },
  { label: '公众号管理' },
  { label: '小程序管理' },
  // { label: '微信小店' },
  { label: '域名管理' },
  { label: '站点管理' },
  { label: '工作台轮播', aliases: ['首页轮播'] }
]

/**
 * 外部租户工作台侧栏不可见的「工作台专属」标题。
 * 故意不含「域名管理/站点管理」：与小程序/公众号同属门户自配；业务模块侧栏同名由
 * 后端 scoped module_code 收口，勿靠标题黑名单误伤。
 */
export const EXTERNAL_TENANT_HIDDEN_MENU_TITLES = new Set([
  '仪表盘',
  '总裁驾驶舱',
  '基础设施',
  '工作流程',
  '商家运营',
  '商家主体',
  '门户解决方案',
  '微信小店'
  // 支付管理 / 公众号管理 / 域名管理：租户自配通道，勿藏
])

/** remaining.ts 中仅内部可见的门户壳 / 工作台大屏静态路由 path 前缀。域名/站点留给外部自配。 */
export const EXTERNAL_TENANT_HIDDEN_STATIC_PATHS = [
  '/wechat-shop',
  '/ceo-cockpit',
  '/ceo-cockpit-view',
  '/dashboard-view'
]

// 工作台左侧菜单分组：保留原有菜单目录，仅增加「概览 / 业务 / 门户」三个分类
// 未匹配到的菜单项会追加到最后一个分组中
export const workbenchMenuGroups: WorkbenchMenuGroup[] = [
  {
    title: '概览',
    modules: ['首页', '仪表盘', '总裁驾驶舱', '治理工作台', '平台 AI 配置', '平台AI配置', 'AI 平台配置']
  },
  {
    title: '业务',
    modules: [
      '系统管理',
      '基础设施',
      '工作流程',
      '会员管理',
      '会员中心',
      '商家运营',
      '商家主体',
      '渠道代理商',
      '平台分销',
      '支付管理',
      '模板管理',
      '灵工提现',
      '评价管理',
      '内容安全'
    ]
  },
  {
    title: '门户',
    modules: [
      '门户解决方案',
      '公众号管理',
      '小程序管理',
      '微信小店',
      '域名管理',
      '站点管理',
      '工作台轮播',
      '首页轮播'
    ]
  }
]

export const workbenchMenuTitles = workbenchMenuModules.flatMap((item) => [
  item.label,
  ...(item.aliases || [])
])

/** 业务中台三模块（财务/项目/人力）：不在左侧菜单展示，改由 AI中台 应用卡片进入 */
const BUSINESS_CENTER_MODULE_TITLES = ['财务管理', '项目管理', '人力资源']

export const headerTabMenuTitles: Record<string, string[]> = {
  workbench: workbenchMenuTitles.filter((t) => !BUSINESS_CENTER_MODULE_TITLES.includes(t)),
  aiApplicationCenter: workbenchMenuTitles.filter(
    (t) => !BUSINESS_CENTER_MODULE_TITLES.includes(t)
  ),
  aiBusinessCenter: workbenchMenuTitles.filter((t) => !BUSINESS_CENTER_MODULE_TITLES.includes(t)),
  products: workbenchMenuTitles.filter((t) => !BUSINESS_CENTER_MODULE_TITLES.includes(t)),
  help: workbenchMenuTitles.filter((t) => !BUSINESS_CENTER_MODULE_TITLES.includes(t))
}

export const headerTabExtraMenus: Record<string, AppRouteRecordRaw[]> = {
  workbench: [
    {
      path: '/workbench-banner',
      name: 'WorkbenchBannerFallback',
      meta: { title: '工作台轮播', icon: 'ep:picture' }
    }
  ],
  // AI 应用中心：商城 / 配送走应用卡或产品中心进独立壳，勿在主壳侧栏插业务兜底
  // （extra 会绕过 headerTabMenuTitles 白名单，再被丢进「门户」末组，造成假门户菜单）
  aiApplicationCenter: [],
  aiBusinessCenter: [
    // {
    //   path: '/erp',
    //   name: 'ErpFallback',
    //   meta: { title: 'ERP进销存', icon: 'ep:goods' }
    // },
    // {
    //   path: '/wms',
    //   name: 'WmsFallback',
    //   meta: { title: 'WMS仓储系统', icon: 'ep:box' }
    // },
    // {
    //   path: '/mes',
    //   name: 'MesFallback',
    //   meta: { title: 'MES系统', icon: 'ep:cpu' }
    // }
  ]
}
